import React, { useCallback, useEffect, useState } from 'react';
import { text } from '../../../../utils/text';
import { Card, CardHeader } from '../../../common/Card/Card';
import { scenarioSteps, trackScenario } from '../../../../tracking';
import { Layout } from '../../../common/Layout/Layout';
import { getAnbefaltProfilOgPortefølje, LOGGER_ENDPOINT, raadgivningController } from '../../../../utils/api';
import { Action, EgnethetQuestionKey, RaadgivningType, RoutePath, Spareformål, } from '../../../../utils/types';
import { Redirect, useHistory } from 'react-router-dom';
import { useFormContext } from '../../../../context/FormContext';
import { remoteLogError } from '@gjensidige/gpf-js-utils/es6/logging/error-handler';
import { NavigationButtons } from '../../../common/NavigationButtons/NavigationButtons';
import { Text } from '@gjensidige/core-typography';
import { flattenGroupedQuestions, getCustomerAge } from '../../../../utils/utils';
import { flatMapAnswers, RaadgivningForm, RaadgivningInput, } from '@gjensidige/gpf-raadgivning-form';
import { Spacer } from '../../../common/Spacer/Spacer';
const INTRO_STEP = 1;
const RAADGIVNING_STEP = 2;
export const EgnethetForm = () => {
    var _a, _b, _c;
    const [{ raadgivningData, kontoopplysninger, customerData, utbetalingsdetaljer }, dispatch] = useFormContext();
    const [step, setStep] = useState((raadgivningData === null || raadgivningData === void 0 ? void 0 : raadgivningData.egnethet.groups) ? RAADGIVNING_STEP : INTRO_STEP);
    const [{ isFetching }, setFetchState] = useState({ isFetching: false });
    const [submitting, setSubmitting] = useState(false);
    const history = useHistory();
    const dispatchAnswers = useCallback((answers) => {
        dispatch({
            type: Action.SUBMIT_RAADGIVNING_DATA,
            raadgivningData: Object.assign(Object.assign({}, raadgivningData), { egnethet: Object.assign(Object.assign({}, raadgivningData.egnethet), { answers }) }),
        });
    }, [dispatch, raadgivningData]);
    const { spareformål } = kontoopplysninger;
    const questionGroups = (_b = (_a = raadgivningData === null || raadgivningData === void 0 ? void 0 : raadgivningData.egnethet) === null || _a === void 0 ? void 0 : _a.groups) !== null && _b !== void 0 ? _b : [];
    const raadgivningType = spareformål === Spareformål.PENSJON
        ? RaadgivningType.SELVBETJENT_FONDSKONTO_PENSJON
        : RaadgivningType.SELVBETJENT_FONDSKONTO_ANNEN_SPARING;
    const includeTidshorisontQuestion = spareformål ? includeTidshorisontQuestions(spareformål, customerData) : false;
    const filteredQuestion = filterQuestions(flattenGroupedQuestions(questionGroups), includeTidshorisontQuestion);
    useEffect(() => {
        trackScenario(scenarioSteps.RAADGIVNING_EGNETHET);
    }, []);
    useEffect(() => {
        if (!raadgivningData.ref && raadgivningType) {
            setFetchState({ isFetching: true });
            raadgivningController.startAndGetRaadgivning(raadgivningType).then((result) => {
                if (result.isOk()) {
                    dispatch({
                        type: Action.SUBMIT_RAADGIVNING_DATA,
                        raadgivningData: Object.assign(Object.assign({}, result.value), { fatca: Object.assign(Object.assign({}, result.value.fatca), { answers: getFatcaAnswers(raadgivningData, result.value) }) }),
                    });
                }
                else if (result.isErr()) {
                    setFetchState({ isFetching: false, error: result.error });
                    void remoteLogError(LOGGER_ENDPOINT, new Error(result.error.message));
                }
                setFetchState({ isFetching: false });
            });
        }
    }, [raadgivningType, raadgivningData, customerData.ssn, dispatch]);
    if (!spareformål)
        return React.createElement(Redirect, { to: RoutePath.KONTO });
    const prev = (answers) => {
        if (step == RAADGIVNING_STEP && answers) {
            dispatchAnswers(flatMapAnswers(answers));
            setStep(INTRO_STEP);
        }
        else {
            history.push(RoutePath.AMOUNT);
        }
    };
    const next = () => setStep(RAADGIVNING_STEP);
    const submit = (answers) => {
        if (raadgivningData === null || raadgivningData === void 0 ? void 0 : raadgivningData.ref) {
            setSubmitting(true);
            const raadgivning = {
                type: raadgivningType,
                answers: getAnswerPayload(answers, questionGroups, includeTidshorisontQuestion),
            };
            raadgivningController.saveEgnethetstest(raadgivningData.ref, raadgivning).then((result) => {
                if (result.isOk()) {
                    dispatchAnswers(raadgivning.answers);
                    getAnbefaltProfilOgPortefølje(result.value.ref, utbetalingsdetaljer.uttaksalder).then((result) => {
                        setSubmitting(false);
                        if (result.isOk()) {
                            dispatch({
                                type: Action.SUBMIT_IDD_ANBEFALING,
                                anbefaling: result.value,
                            });
                            history.push(RoutePath.ANBEFALT_PROFIL);
                        }
                        else if (result.isErr()) {
                            void remoteLogError(LOGGER_ENDPOINT, new Error(result.error.message));
                        }
                    });
                }
                else if (result.isErr()) {
                    setSubmitting(false);
                    void remoteLogError(LOGGER_ENDPOINT, new Error(result.error.message));
                }
            });
        }
    };
    return (React.createElement(Layout, { title: text.LAYOUT.START_SAVING.TITLE.no },
        React.createElement(Card, { step: 3, loading: isFetching && step != INTRO_STEP },
            step === 1 && (React.createElement(React.Fragment, null,
                React.createElement(CardHeader, { title: text.FORM.RAADGIVNING_EGNETHET.TITLE.no, subtitle: text.FORM.RAADGIVNING_EGNETHET.DESCRIPTION.no }),
                React.createElement(Text, null, text.FORM.RAADGIVNING_EGNETHET.TIDSBRUK.no),
                React.createElement(NavigationButtons, { isSubmit: false, onPrevious: prev, onNext: next, loading: submitting }))),
            step === 2 && (React.createElement(RaadgivningForm, { questions: filteredQuestion, onSubmit: (answers) => submit(answers), initialAnswers: (_c = raadgivningData === null || raadgivningData === void 0 ? void 0 : raadgivningData.egnethet) === null || _c === void 0 ? void 0 : _c.answers }, (control, answers, updateAnswer) => (React.createElement(Spacer, { child: 'lg' },
                filteredQuestion.map((question) => (React.createElement(RaadgivningInput, { key: question.id, question: question, answers: answers[question.id], updateAnswer: updateAnswer, control: control, labelSize: 'h4' }))),
                React.createElement(NavigationButtons, { isSubmit: true, onPrevious: () => prev(answers), loading: submitting }))))))));
};
const getFatcaAnswers = (prevData, raadgivning) => {
    // bruk svar avgitt i fondskonto skjema fremfor nytt resultat siden fatca ikke er avhengig av type
    if (prevData.fatca.answers.length > 0) {
        return prevData.fatca.answers;
    }
    else {
        return raadgivning.fatca.answers;
    }
};
const getAnswerPayload = (answers, groups, includeTidshorisont) => {
    if (includeTidshorisont) {
        return answers;
    }
    else {
        const questions = groups.flatMap((group) => group.questions);
        return [...answers, getDefaultTidshorisontAnswer(questions)];
    }
};
const getDefaultTidshorisontAnswer = (questions) => {
    const question = questions.find((q) => q.key === EgnethetQuestionKey.TIDSHORISONT);
    // default tidshorisont svar for spareformål pensjon er det høyest vektede svaralternativet
    const defaultOption = question === null || question === void 0 ? void 0 : question.options.find((opt) => opt.weight === Math.max(...question.options.map((it) => it.weight)));
    if (!question || !defaultOption) {
        throw new Error(text.FORM.RAADGIVNING_EGNETHET.DEFAULT_ANSWER_MISSING.no);
    }
    return {
        questionId: question.id,
        optionId: defaultOption.id,
    };
};
// vi velger tidshorisont på vegne av kunden hvis spareformål er pensjon og personen har mer enn 5 år igjen til pensjonering
const includeTidshorisontQuestions = (spareformål, customerData) => {
    if (spareformål !== Spareformål.PENSJON) {
        return true;
    }
    return getCustomerAge(customerData.ssn) >= 58;
};
const filterQuestions = (questions, includeTidshorisont) => {
    return includeTidshorisont ? questions : questions.filter((q) => q.key !== EgnethetQuestionKey.TIDSHORISONT);
};
